import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Folder, X } from "lucide-react";
import { Message } from "@/i18n/Message";
import { t, useLocale } from "@/i18n/locale";
import { isAtoAppProxySessionUrl } from "@/api/launchClient";
import type { PersonalApp } from "./types";
import "./instanceFiles.css";

/** Only the resolved private Instance's own platform document. No app path,
 * token, descriptor, parent origin or Surface credential crosses this boundary. */
export function instanceFilesDocumentUrl(app: PersonalApp): string | null {
  if (
    app.status !== "active" ||
    !["owner", "editor"].includes(app.role) ||
    app.public_guest_try ||
    app.public_try_expires_at != null ||
    !/^cinst_[A-Za-z0-9]+$/.test(app.instance_id) ||
    app.host_slug.length > 63 ||
    !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(app.host_slug) ||
    app.app_url.trim() !== app.app_url ||
    app.app_url.includes("\\") ||
    /[\u0000-\u0020]/.test(app.app_url)
  )
    return null;
  try {
    const url = new URL(app.app_url);
    if (
      !isAtoAppProxySessionUrl(app.app_url) ||
      url.username ||
      url.password ||
      url.hostname.split(".")[0] !== app.host_slug ||
      url.pathname !== "/" ||
      url.search ||
      url.hash ||
      (app.app_url !== url.origin && app.app_url !== `${url.origin}/`)
    )
      return null;
    return `${url.origin}/__ato/files`;
  } catch {
    return null;
  }
}

/** The document owns same-origin Assets calls and their errors/cancellation.
 * The PWA never sends cross-origin file mutations or trusts iframe messages. */
export default function InstanceFilesAction({ app }: { app: PersonalApp }) {
  useLocale();
  const url = instanceFilesDocumentUrl(app);
  const identity = `${app.instance_id}:${url ?? ""}`;
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => setOpenedFor(null), [identity]);
  if (!url) return null;
  return (
    <>
      <button
        type="button"
        ref={trigger}
        className="btn instance-files-action"
        onClick={() => setOpenedFor(identity)}
      >
        <Folder size={15} aria-hidden />
        <Message message={"Files"} />
      </button>
      {openedFor === identity
        ? createPortal(
            <FilesDialog
              key={identity}
              url={url}
              onClose={() => {
                setOpenedFor(null);
                trigger.current?.focus();
              }}
            />,
            document.body,
          )
        : null}
    </>
  );
}

function FilesDialog({ url, onClose }: { url: string; onClose: () => void }) {
  const title = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const [failed, setFailed] = useState(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (typeof node.showModal === "function") node.showModal();
    else node.setAttribute("open", "");
    return () => {
      if (node.open && typeof node.close === "function") node.close();
    };
  }, []);
  useEffect(() => {
    const node = frame.current;
    if (!node) return;
    const fail = () => setFailed(true);
    node.addEventListener("error", fail);
    return () => node.removeEventListener("error", fail);
  }, [failed, revision]);
  return (
    <dialog
      ref={dialog}
      className="instance-files-dialog"
      aria-labelledby={title}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header className="instance-files-head">
        <h2 id={title}>
          <Message message={"Files"} />
        </h2>
        <button
          type="button"
          className="btn"
          aria-label={t("Close")}
          onClick={onClose}
        >
          <X size={18} aria-hidden />
        </button>
      </header>
      <p className="instance-files-note">
        <Message
          message={
            "Uploads are available to the next run. Outputs are saved after the app stops."
          }
        />
      </p>
      {failed ? (
        <div className="instance-files-error" role="alert">
          <p>
            <Message message={"Files couldn’t be loaded. Try again."} />
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setFailed(false);
              setRevision((value) => value + 1);
            }}
          >
            <Message message={"Retry"} />
          </button>
        </div>
      ) : (
        <iframe
          ref={frame}
          key={revision}
          className="instance-files-frame"
          title={t("Files")}
          src={url}
          sandbox="allow-scripts allow-same-origin allow-downloads"
          referrerPolicy="no-referrer"
        />
      )}
    </dialog>
  );
}
